/**
 * context.mjs — the window, and what physically will not fit in it.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 2.
 *
 * ── THE SENTENCE THE INSTRUMENT EXISTS FOR ────────────────────────────────────────────────────
 * §2: "If the prompt exceeds the context window, the AI does not 'remember less' — it physically
 * cannot see the part that does not fit."
 *
 * That word — physically — is doing the work, and it is the one thing a paragraph cannot deliver.
 * A learner nods at "the window is finite" and still believes, next week, that a long chat means
 * the AI is being lazy or has forgotten. Watching a block fall out of the window and disappear from
 * WHAT THE MODEL SEES is a different kind of knowing. So the lab has two panes: what you sent, and
 * what arrived. The gap between them is the chapter.
 *
 * ── THE ARITHMETIC IS HIS, NOT A TOKENIZER I WROTE ────────────────────────────────────────────
 * §2 gives two size pairs — "4,000-token context window — roughly 3,000 words" and "200,000 tokens
 * — roughly 150,000 words". Both are exactly 4 tokens per 3 words, so the ratio is derived from his
 * own figures rather than measured with a real tokenizer, which would be a different number and
 * would put the lab quietly at odds with the page beside it. The gate asserts both pairs still
 * agree; if he ever revises one, the ratio stops being derivable and the gate says so.
 */

export const SOURCE_2 = 'CodeAI101 module 02 §2 "The context window — the AI\'s working memory"';
export const SOURCE_3 = 'CodeAI101 module 02 §3 "System, user, and assistant — the three voices"';
export const SOURCE_4 = 'CodeAI101 module 02 §4 "No context vs good context — the concrete contrast"';

export const PHYSICALLY = 'If the prompt exceeds the context window, the AI does not "remember less" — it physically cannot see the part that does not fit.';
export const NOT_TIRED = 'This is why long chats with an AI sometimes start to feel forgetful. The product is silently dropping older messages to keep within the context window. The AI is not "tired"; it physically cannot see what it could see ten minutes ago.';
export const ORDER_MATTERS = 'What is near the end of the prompt (closest to where the AI starts generating) usually has more weight than what is near the start.';
export const NOT_THE_MODEL = 'The difference is not the model. It is what the model can see.';

/** §2's three sizes, with the word counts he gives beside them. */
export const WINDOWS = Object.freeze([
  { id: 'small', tokens: 4000, words: 3000, label: 'a small model', note: 'Enough for a chat and a short document.' },
  { id: 'frontier', tokens: 200000, words: 150000, label: 'a modern frontier model', note: 'roughly 150,000 words, a 400-page book.' },
  { id: 'long', tokens: 1000000, words: null, label: 'a specialised long-context model', note: 'a million tokens — a small library.' },
]);

/**
 * Tokens per word, DERIVED from his own two pairs rather than declared. Both give 4/3 exactly.
 * Computed here so that a future edit to either figure changes the lab's arithmetic instead of
 * leaving a stale constant that disagrees with the page.
 */
export const tokensPerWord = (windows = WINDOWS) => {
  const pairs = windows.filter((w) => w.words);
  const ratios = pairs.map((w) => w.tokens / w.words);
  const allAgree = ratios.every((r) => Math.abs(r - ratios[0]) < 1e-9);
  return { value: ratios[0], allAgree, from: pairs.map((w) => `${w.tokens}/${w.words}`) };
};

export const countTokens = (text) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.round(words * tokensPerWord().value);
};

/** §3's three voices, quoted. */
export const VOICES = Object.freeze([
  /* THE WHOLE PASSAGE, including his example. My first version jumped from "invisible to the user"
     straight to "The system message defines…", eliding the sample system prompt in between and
     joining two non-adjacent sentences with no ellipsis. That is the same paraphrase-by-omission
     the courier lab was caught on: a quotation shortened is a quotation edited. */
  { id: 'system', name: 'System message',
    quote: 'set by the product, usually invisible to the user. "You are a helpful customer service assistant for [company]. Be polite. Do not discuss [topics]. Format your responses as [pattern]." The system message defines the AI\'s persona and behaviour for the whole conversation. The user cannot normally see or edit it.' },
  { id: 'user', name: 'User messages', quote: 'what you type. The actual questions and inputs you provide.' },
  { id: 'assistant', name: 'Assistant messages',
    quote: 'what the AI produced in previous turns. These get included as part of the prompt for the next turn, so the AI "remembers" its own replies.' },
]);
export const ONE_STREAM = 'The three voices are concatenated, separated by markers the model has been trained to recognise.';

/** §4's two prompts, verbatim. The whole of the contrast is that these ask the same thing. */
export const PROMPT_A = 'Should I use a database or a file for this?';
export const PROMPT_B = 'I am building a personal grocery-tracker app for my own phone, for offline use. I expect to add five to ten entries per day, total around 2,000 entries per year. I will run reports like "total spent this month" and "amount by category." I am building solo, on a budget. Should I store the data in a JSON file or in a SQLite database? What are the trade-offs for my specific situation?';
export const A_GIVES = 'The AI has no idea what "this" is. It will produce a generic essay on the trade-offs between databases and files. Some of it will be true. Most of it will be useless.';
export const B_GIVES = 'The AI now has enough to answer specifically.';
export const WITHHOLDS = 'Prompt A withholds the substrate; Prompt B provides it.';

/**
 * Pack blocks into a window and report what did not fit.
 *
 * TRUNCATION DROPS THE OLDEST, which is his mechanism — "drop the oldest messages" — and it is why
 * a long chat feels forgetful rather than confused: the beginning goes first, so the AI still
 * sounds fluent while having lost what you told it at the start. The system message is kept because
 * §3 says the product sets it for the whole conversation; a real product that dropped it would stop
 * being that product.
 */
export function pack(blocks, limitTokens) {
  const withCost = blocks.map((b) => ({ ...b, tokens: countTokens(b.text) }));
  const pinned = withCost.filter((b) => b.voice === 'system');
  const rest = withCost.filter((b) => b.voice !== 'system');
  let used = pinned.reduce((t, b) => t + b.tokens, 0);
  const kept = [];
  /* Newest first — the end of the conversation is what survives. */
  for (let i = rest.length - 1; i >= 0; i -= 1) {
    if (used + rest[i].tokens <= limitTokens) { used += rest[i].tokens; kept.unshift(rest[i]); }
  }
  const keptIds = new Set([...pinned, ...kept].map((b) => b.id));
  return {
    sent: withCost,
    seen: [...pinned, ...kept],
    dropped: withCost.filter((b) => !keptIds.has(b.id)),
    usedTokens: used,
    sentTokens: withCost.reduce((t, b) => t + b.tokens, 0),
    limitTokens,
    overflowed: withCost.some((b) => !keptIds.has(b.id)),
  };
}

/** Weight by position: §2 says the end matters more. Reported as a rank, never as a percentage —
 *  he says "usually has more weight" and gives no number, so neither does this. */
export const positionNote = 'nearest the end — usually weighed most';
